import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Image as ImageIcon, Sparkles, Download, Save, RotateCcw, Upload, AlertTriangle, Wand2, Crown } from "lucide-react";
import { toast } from "sonner";
import * as htmlToImage from "html-to-image";

const FORMATS = [
  { v: "square", l: "Square post (1:1)", w: 320, h: 320, px: 1080, py: 1080 },
  { v: "portrait", l: "Portrait post (4:5)", w: 320, h: 400, px: 1080, py: 1350 },
  { v: "story", l: "Story (9:16)", w: 300, h: 533, px: 1080, py: 1920 },
];
const PURPOSES = [
  { v: "offer", l: "Promote an offer" }, { v: "event", l: "Promote an event" },
  { v: "opportunity", l: "Collaboration opportunity" }, { v: "product", l: "A product" }, { v: "service", l: "A service" },
];
const STYLES = [{ v: "clean", l: "Clean" }, { v: "bold", l: "Bold" }, { v: "elegant", l: "Elegant" }, { v: "playful", l: "Playful" }];
const fmtOf = (v) => FORMATS.find((f) => f.v === v) || FORMATS[0];

const EMPTY = {
  purpose: "offer", format: "square", route: "ai", style: "clean",
  headline: "", supporting_text: "", date_text: "", price_offer: "", cta: "",
  brand_colours: ["#0F766E", "#F59E0B"], photo_url: "", extra_context: "",
};
const DEFAULT_DESIGN = {
  backgroundDataUrl: "", backgroundUrl: "", logoDataUrl: "", logoUrl: "",
  textColor: "#ffffff", scrim: 0.35, vAlign: "bottom", align: "center", fontScale: 1, showLogo: true,
};

async function urlToDataUrl(url) {
  const path = (url || "").replace(/^\/api/, ""); // api client already prefixes /api
  const res = await api.get(path, { responseType: "blob" });
  return await new Promise((resolve) => { const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.readAsDataURL(res.data); });
}
function fileToDataUrl(file) {
  return new Promise((resolve) => { const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.readAsDataURL(file); });
}

export function ImageCreator({ status, onStatusChange, initial, onSavedDraft }) {
  const [view, setView] = useState(initial ? "editor" : "form");
  const [form, setForm] = useState(initial?.brief ? { ...EMPTY, ...initial.brief } : EMPTY);
  const [design, setDesign] = useState(initial?.design ? { ...DEFAULT_DESIGN, ...initial.design } : DEFAULT_DESIGN);
  const [busy, setBusy] = useState(false);
  const [jobStatus, setJobStatus] = useState(null); // pending|generating|saving
  const [jobError, setJobError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [opps, setOpps] = useState([]);
  const [savedId, setSavedId] = useState(initial?.id || null);
  const canvasRef = useRef(null);
  const pollingRef = useRef(false);

  useEffect(() => { api.get("/opportunities/mine").then((r) => setOpps(r.data.opportunities || [])).catch(() => {}); }, []);
  // Resume an in-flight job if the user left and came back.
  useEffect(() => {
    if (initial) return; // editing a saved draft, not generating
    api.get("/ai/marketing/image/jobs/active").then((r) => {
      if (r.data.job) { setBusy(true); pollJob(r.data.job.id); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // If reopening a draft whose background is only stored as a URL, hydrate it to a dataURL for preview/export.
    if (initial?.design?.backgroundUrl && !initial?.design?.backgroundDataUrl) {
      urlToDataUrl(initial.design.backgroundUrl).then((d) => setDesign((x) => ({ ...x, backgroundDataUrl: d }))).catch(() => {});
    }
    if (initial?.design?.logoUrl && !initial?.design?.logoDataUrl) {
      urlToDataUrl(initial.design.logoUrl).then((d) => setDesign((x) => ({ ...x, logoDataUrl: d }))).catch(() => {});
    }
  }, [initial]);

  const isFree = status && !status.is_pro;
  const noCredits = status && status.is_pro && (status.total_available ?? status.remaining_this_month) === 0 && !busy;
  const imgUnavailable = status && !status.image_available;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setD = (k, v) => setDesign((d) => ({ ...d, [k]: v }));

  const prefillFromOpp = (id) => {
    const o = opps.find((x) => x.id === id);
    if (!o) return;
    setForm((f) => ({ ...f, purpose: "opportunity", headline: o.title || f.headline,
      supporting_text: (o.description || "").slice(0, 160) || f.supporting_text,
      cta: f.cta || "Apply now" }));
    toast.success("Prefilled from opportunity");
  };

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.url; // /api/files/...
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    setD("logoDataUrl", dataUrl);
    try { const url = await uploadFile(file); setD("logoUrl", url); } catch { toast.error("Logo upload failed"); }
  };
  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    setD("backgroundDataUrl", dataUrl);
    try { const url = await uploadFile(file); setD("backgroundUrl", url); set("photo_url", url); } catch { toast.error("Photo upload failed"); }
  };

  const STATUS_LABEL = { pending: "Queued…", generating: "Creating your image…", saving: "Saving to your library…" };

  const pollJob = async (jobId) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setJobError(null); setJobStatus("pending");
    let tries = 0;
    while (pollingRef.current) {
      await new Promise((r) => setTimeout(r, 2500));
      tries += 1;
      try {
        const r = await api.get(`/ai/marketing/image/jobs/${jobId}`);
        const j = r.data;
        setJobStatus(j.status);
        if (["completed", "failed", "timed_out"].includes(j.status)) {
          pollingRef.current = false;
          if (j.marketing_status) onStatusChange?.(j.marketing_status);
          if (j.status === "completed") {
            try {
              const dataUrl = await urlToDataUrl(j.image_url);
              setDesign((d) => ({ ...d, backgroundDataUrl: dataUrl, backgroundUrl: j.image_url }));
            } catch { /* editor can still hydrate later */ }
            setSavedId(null); setView("editor");
            toast.success("Image ready and saved to your library — edit your text and download");
          } else {
            setJobError(j.error || "Generation failed. Your credit was not used.");
          }
          setBusy(false);
          break;
        }
      } catch (e) {
        if (tries > 90) { // ~4 min safety net (backend times out well before this)
          pollingRef.current = false;
          setJobError("We lost track of this image. Your credit was not used — please try again.");
          setBusy(false);
          break;
        }
      }
    }
  };

  const generate = async (isRevision = false) => {
    if (form.route === "photo") {
      if (!design.backgroundDataUrl) { toast.error("Please upload a photo first"); return; }
      setView("editor"); return; // photo route uses no AI credit
    }
    if (isRevision && !window.confirm("Generating a new AI image uses one of your monthly credits (or a bonus credit). Continue?")) return;
    setBusy(true); setJobError(null); setJobStatus("pending");
    try {
      const requestId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
      const r = await api.post("/ai/marketing/image/generate", { ...form, request_id: requestId });
      pollJob(r.data.id);
    } catch (e) {
      setBusy(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start generation — this attempt was not counted");
    }
  };

  const exportPng = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const f = fmtOf(form.format);
      const ratio = f.px / f.w;
      const dataUrl = await htmlToImage.toPng(canvasRef.current, { pixelRatio: ratio, cacheBust: true });
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `nearfluence-${form.format}-${Date.now()}.png`; a.click();
      toast.success("High-resolution PNG downloaded");
    } catch { toast.error("Could not export the image"); }
    finally { setExporting(false); }
  };

  const saveDraft = async () => {
    try {
      let assetUrl = design.backgroundUrl;
      // Render + upload the composed PNG so the finished asset is saved too.
      if (canvasRef.current) {
        try {
          const f = fmtOf(form.format);
          const blob = await htmlToImage.toBlob(canvasRef.current, { pixelRatio: f.px / f.w, cacheBust: true });
          if (blob) {
            const file = new File([blob], "design.png", { type: "image/png" });
            assetUrl = await uploadFile(file);
          }
        } catch { /* keep background as fallback asset */ }
      }
      const persistDesign = { ...design, backgroundDataUrl: "", logoDataUrl: "", assetUrl };
      const payload = { title: (form.headline || form.extra_context || "Image design").slice(0, 80),
        content_type: form.format, brief: form, pack: { kind: "image", design: persistDesign, asset_url: assetUrl }, status: "draft" };
      if (savedId) await api.put(`/ai/marketing/drafts/${savedId}`, payload);
      else { const r = await api.post("/ai/marketing/drafts", payload); setSavedId(r.data.id); }
      toast.success("Saved to your drafts");
      onSavedDraft?.();
    } catch { toast.error("Could not save the design"); }
  };

  if (isFree) {
    return (
      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="image-pro-upsell">
        <p className="flex items-center gap-2 font-semibold text-amber-800"><Crown className="h-4 w-4" /> The Image Creator is a Business Pro feature</p>
        <p className="mt-1 text-sm text-amber-700">Turn your offers and events into polished, on-brand marketing graphics. Business Pro includes 3 AI generations each month, shared across copy and images.</p>
        <Button data-testid="image-upsell-upgrade" disabled className="mt-3 rounded-full bg-indigo-600 opacity-70">Upgrade to Pro — Coming soon</Button>
      </div>
    );
  }

  if (imgUnavailable) {
    return (
      <div className="mt-4 flex items-start gap-2 rounded-2xl border border-zinc-200 bg-white p-4 text-sm text-zinc-600" data-testid="image-unavailable">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <span><b>Image generation is currently unavailable.</b><br />{status?.image_unavailable_reason || "Please try again later."}</span>
      </div>
    );
  }

  const f = fmtOf(form.format);

  return (
    <div className="mt-4">
      {view === "form" && (
        <div className="space-y-3" data-testid="image-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Purpose</Label>
              <Select value={form.purpose} onValueChange={(v) => set("purpose", v)}>
                <SelectTrigger data-testid="img-purpose" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{PURPOSES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Format</Label>
              <Select value={form.format} onValueChange={(v) => set("format", v)}>
                <SelectTrigger data-testid="img-format" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMATS.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {opps.length > 0 && (
            <div><Label>Prefill from an opportunity (optional)</Label>
              <Select onValueChange={prefillFromOpp}>
                <SelectTrigger data-testid="img-opp" className="mt-1"><SelectValue placeholder="Choose an opportunity…" /></SelectTrigger>
                <SelectContent>{opps.map((o) => <SelectItem key={o.id} value={o.id}>{o.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div><Label>Headline</Label><Input data-testid="img-headline" value={form.headline} onChange={(e) => set("headline", e.target.value)} className="mt-1" placeholder="e.g. Weekend Brunch" /></div>
          <div><Label>Supporting text (optional)</Label><Input data-testid="img-supporting" value={form.supporting_text} onChange={(e) => set("supporting_text", e.target.value)} className="mt-1" placeholder="e.g. Fresh pancakes all weekend" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date (optional)</Label><Input data-testid="img-date" value={form.date_text} onChange={(e) => set("date_text", e.target.value)} className="mt-1" placeholder="Sat & Sun" /></div>
            <div><Label>Price / offer (optional)</Label><Input data-testid="img-price" value={form.price_offer} onChange={(e) => set("price_offer", e.target.value)} className="mt-1" placeholder="2-for-1" /></div>
          </div>
          <div><Label>Call to action (optional)</Label><Input data-testid="img-cta" value={form.cta} onChange={(e) => set("cta", e.target.value)} className="mt-1" placeholder="Book a table" /></div>

          <div><Label>Visual route</Label>
            <div className="mt-1 flex gap-2">
              <button type="button" data-testid="img-route-ai" onClick={() => set("route", "ai")} className={`flex-1 rounded-xl border p-2 text-sm ${form.route === "ai" ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-zinc-200"}`}><Wand2 className="mr-1 inline h-4 w-4" /> AI-generated visual</button>
              <button type="button" data-testid="img-route-photo" onClick={() => set("route", "photo")} className={`flex-1 rounded-xl border p-2 text-sm ${form.route === "photo" ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-zinc-200"}`}><ImageIcon className="mr-1 inline h-4 w-4" /> Use my own photo</button>
            </div>
          </div>

          {form.route === "ai" ? (
            <>
              <div><Label>Style</Label>
                <Select value={form.style} onValueChange={(v) => set("style", v)}>
                  <SelectTrigger data-testid="img-style" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STYLES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Describe the visual (optional)</Label><Textarea data-testid="img-extra" value={form.extra_context} onChange={(e) => set("extra_context", e.target.value)} rows={2} className="mt-1" placeholder="e.g. cosy cafe table with coffee and pancakes, morning light" /></div>
            </>
          ) : (
            <div><Label>Upload your photo</Label>
              <label className="mt-1 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-300 p-4 text-sm text-zinc-500">
                <Upload className="h-4 w-4" /> {design.backgroundDataUrl ? "Photo added — tap to replace" : "Choose a product or venue photo"}
                <input data-testid="img-photo-input" type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </label>
            </div>
          )}

          <div><Label>Business logo (optional, kept intact)</Label>
            <label className="mt-1 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-300 p-3 text-sm text-zinc-500">
              <Upload className="h-4 w-4" /> {design.logoDataUrl ? "Logo added — tap to replace" : "Upload your logo"}
              <input data-testid="img-logo-input" type="file" accept="image/*" className="hidden" onChange={onLogo} />
            </label>
          </div>

          <div><Label>Brand colours</Label>
            <div className="mt-1 flex gap-2">
              {form.brand_colours.map((c, i) => (
                <input key={i} data-testid={`img-colour-${i}`} type="color" value={c} onChange={(e) => { const arr = [...form.brand_colours]; arr[i] = e.target.value; set("brand_colours", arr); }} className="h-9 w-12 rounded border border-zinc-200" />
              ))}
            </div>
          </div>

          {(busy || jobError) && (
            <div data-testid="img-job-state" className={`rounded-2xl border p-4 ${jobError ? "border-rose-200 bg-rose-50" : "border-indigo-200 bg-indigo-50"}`}>
              {jobError ? (
                <div>
                  <p className="flex items-center gap-2 font-semibold text-rose-800" data-testid="img-job-error"><AlertTriangle className="h-4 w-4" /> Image generation failed</p>
                  <p className="mt-1 text-sm text-rose-700">{jobError}</p>
                  <Button data-testid="img-retry" onClick={() => generate(false)} className="mt-3 rounded-full bg-indigo-600 hover:bg-indigo-700"><RotateCcw className="mr-1.5 h-4 w-4" /> Try again</Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                  <div>
                    <p className="text-sm font-semibold text-indigo-900" data-testid="img-job-status">{STATUS_LABEL[jobStatus] || "Working…"}</p>
                    <p className="text-xs text-indigo-600">This usually takes 10–20 seconds. You can stay on this page — we'll show it as soon as it's ready.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <Button data-testid="img-generate-btn" disabled={busy || noCredits} onClick={() => generate(false)} className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700">
            {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {STATUS_LABEL[jobStatus] || "Working…"}</> : <><Sparkles className="mr-1.5 h-4 w-4" /> {form.route === "ai" ? "Generate image (uses 1 credit)" : "Create design (no credit used)"}</>}
          </Button>
          {noCredits && <p className="text-center text-xs text-amber-600">No generations left this month — resets on {status?.reset_date || "the 1st"}. You can still edit and download saved designs.</p>}
        </div>
      )}

      {view === "editor" && (
        <div className="space-y-4" data-testid="image-editor">
          <div className="sticky top-16 z-30 -mx-1 flex flex-wrap gap-2 rounded-xl bg-[#FAF9F6]/95 px-1 py-2 backdrop-blur">
            <Button size="sm" data-testid="img-download" onClick={exportPng} disabled={exporting} className="rounded-full bg-emerald-600 hover:bg-emerald-700">{exporting ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />} Download PNG</Button>
            <Button size="sm" variant="outline" data-testid="img-save-draft" onClick={saveDraft} className="rounded-full"><Save className="mr-1 h-3.5 w-3.5" /> Save draft</Button>
            {form.route === "ai" && <Button size="sm" variant="outline" data-testid="img-revise" onClick={() => generate(true)} disabled={busy || noCredits} className="rounded-full">{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1 h-3.5 w-3.5" />} New AI image (1 credit)</Button>}
            <Button size="sm" variant="outline" data-testid="img-back-form" onClick={() => setView("form")} className="rounded-full">Edit brief</Button>
          </div>

          {/* Preview canvas — text is overlaid as editable layers; no AI call to edit */}
          <div className="flex justify-center">
            <div ref={canvasRef} data-testid="image-canvas" style={{ width: f.w, height: f.h, position: "relative", overflow: "hidden", background: "#111827" }} className="rounded-xl shadow-lg">
              {design.backgroundDataUrl && <img src={design.backgroundDataUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />}
              <div style={{ position: "absolute", inset: 0, background: `rgba(0,0,0,${design.scrim})` }} />
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                justifyContent: design.vAlign === "top" ? "flex-start" : design.vAlign === "middle" ? "center" : "flex-end",
                alignItems: design.align === "left" ? "flex-start" : design.align === "right" ? "flex-end" : "center",
                textAlign: design.align, padding: f.w * 0.07, gap: f.w * 0.02, color: design.textColor }}>
                {design.showLogo && design.logoDataUrl && <img src={design.logoDataUrl} alt="logo" style={{ maxHeight: f.h * 0.14, maxWidth: f.w * 0.4, objectFit: "contain", marginBottom: f.w * 0.02 }} />}
                {form.headline && <div style={{ fontWeight: 800, lineHeight: 1.05, fontSize: f.w * 0.11 * design.fontScale, textShadow: "0 1px 8px rgba(0,0,0,0.35)" }}>{form.headline}</div>}
                {form.supporting_text && <div style={{ fontWeight: 500, fontSize: f.w * 0.05 * design.fontScale, opacity: 0.95 }}>{form.supporting_text}</div>}
                {(form.date_text || form.price_offer) && <div style={{ fontWeight: 700, fontSize: f.w * 0.06 * design.fontScale }}>{[form.date_text, form.price_offer].filter(Boolean).join("  •  ")}</div>}
                {form.cta && <div style={{ marginTop: f.w * 0.02, display: "inline-block", background: form.brand_colours[0] || "#0F766E", color: "#fff", fontWeight: 700, fontSize: f.w * 0.045 * design.fontScale, padding: `${f.w * 0.025}px ${f.w * 0.05}px`, borderRadius: 999 }}>{form.cta}</div>}
              </div>
            </div>
          </div>

          {/* Editing controls (no credit used) */}
          <div className="grid grid-cols-2 gap-3 rounded-2xl border border-zinc-200 bg-white p-3">
            <div className="col-span-2"><Label className="text-xs">Headline</Label><Input data-testid="edit-headline" value={form.headline} onChange={(e) => set("headline", e.target.value)} className="mt-1" /></div>
            <div className="col-span-2"><Label className="text-xs">Supporting text</Label><Input data-testid="edit-supporting" value={form.supporting_text} onChange={(e) => set("supporting_text", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Date</Label><Input data-testid="edit-date" value={form.date_text} onChange={(e) => set("date_text", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Price / offer</Label><Input data-testid="edit-price" value={form.price_offer} onChange={(e) => set("price_offer", e.target.value)} className="mt-1" /></div>
            <div className="col-span-2"><Label className="text-xs">Call to action</Label><Input data-testid="edit-cta" value={form.cta} onChange={(e) => set("cta", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Text colour</Label><input data-testid="edit-textcolor" type="color" value={design.textColor} onChange={(e) => setD("textColor", e.target.value)} className="mt-1 h-9 w-full rounded border border-zinc-200" /></div>
            <div><Label className="text-xs">CTA colour</Label><input data-testid="edit-ctacolor" type="color" value={form.brand_colours[0] || "#0F766E"} onChange={(e) => { const arr = [...form.brand_colours]; arr[0] = e.target.value; set("brand_colours", arr); }} className="mt-1 h-9 w-full rounded border border-zinc-200" /></div>
            <div><Label className="text-xs">Text position</Label>
              <Select value={design.vAlign} onValueChange={(v) => setD("vAlign", v)}>
                <SelectTrigger data-testid="edit-valign" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="top">Top</SelectItem><SelectItem value="middle">Middle</SelectItem><SelectItem value="bottom">Bottom</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Text align</Label>
              <Select value={design.align} onValueChange={(v) => setD("align", v)}>
                <SelectTrigger data-testid="edit-align" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Centre</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Darken background ({Math.round(design.scrim * 100)}%)</Label>
              <input data-testid="edit-scrim" type="range" min="0" max="0.7" step="0.05" value={design.scrim} onChange={(e) => setD("scrim", parseFloat(e.target.value))} className="mt-1 w-full" /></div>
            <div className="col-span-2"><Label className="text-xs">Text size ({Math.round(design.fontScale * 100)}%)</Label>
              <input data-testid="edit-fontscale" type="range" min="0.7" max="1.5" step="0.05" value={design.fontScale} onChange={(e) => setD("fontScale", parseFloat(e.target.value))} className="mt-1 w-full" /></div>
          </div>
          <p className="rounded-lg bg-zinc-100 px-3 py-2 text-xs text-zinc-500">Editing text, colours and layout is free — only generating a new AI image uses a credit. Please double-check spelling, dates and prices before downloading. Nothing is posted to Instagram automatically.</p>
        </div>
      )}
    </div>
  );
}
